import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { DOCTORS } from "@/data/doctors";
import { waLink, WA_MESSAGES } from "@/utils/whatsapp";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const TIME_SLOTS = ["Morning (10 AM – 1 PM)", "Afternoon (1 PM – 4 PM)", "Evening (4 PM – 7 PM)"];

const initial = { name: "", mobile: "", doctor: "Any available doctor", date: "", time: TIME_SLOTS[0], reason: "" };

export const AppointmentForm = () => {
  const [form, setForm] = useState(initial);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter the patient name and mobile number.");
      return;
    }
    if (!/^[0-9+\s-]{10,15}$/.test(form.mobile.trim())) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (!form.date) {
      toast.error("Please choose a preferred date.");
      return;
    }
    const message = WA_MESSAGES.appointmentRequest({ ...form, name: form.name.trim(), mobile: form.mobile.trim() });
    window.open(waLink(message), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your appointment request…");
  };

  const fieldCls =
    "mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 transition-colors focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-100";

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="appointment-form" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="appt-name">Patient Name *</Label>
          <Input id="appt-name" value={form.name} onChange={set("name")} placeholder="Full name" className={fieldCls} data-testid="appointment-name-input" />
        </div>
        <div>
          <Label htmlFor="appt-mobile">Mobile Number *</Label>
          <Input id="appt-mobile" type="tel" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile number" className={fieldCls} data-testid="appointment-mobile-input" />
        </div>
        <div>
          <Label htmlFor="appt-doctor">Preferred Doctor</Label>
          <select id="appt-doctor" value={form.doctor} onChange={set("doctor")} className={fieldCls} data-testid="appointment-doctor-select">
            <option>Any available doctor</option>
            {DOCTORS.map((d) => (
              <option key={d.slug} value={d.name}>{d.name} — {d.specialty}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="appt-date">Preferred Date *</Label>
          <Input id="appt-date" type="date" min={new Date().toISOString().split("T")[0]} value={form.date} onChange={set("date")} className={fieldCls} data-testid="appointment-date-input" />
        </div>
        <div>
          <Label htmlFor="appt-time">Preferred Time</Label>
          <select id="appt-time" value={form.time} onChange={set("time")} className={fieldCls} data-testid="appointment-time-select">
            {TIME_SLOTS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="appt-reason">Reason for Visit</Label>
          <Textarea id="appt-reason" value={form.reason} onChange={set("reason")} rows={3} placeholder="Briefly describe the concern (optional)" className={fieldCls} data-testid="appointment-reason-input" />
        </div>
      </div>

      <button
        type="submit"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#1eb856] hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
        data-testid="appointment-submit-button"
      >
        <Send className="h-4 w-4" aria-hidden="true" /> Request Appointment on WhatsApp
      </button>
      <p className="mt-4 text-center text-xs leading-relaxed text-slate-500" data-testid="appointment-disclaimer">
        Appointment requests are subject to confirmation by the hospital. Your details are not stored on this website —
        the request opens in WhatsApp on your own device.
      </p>
    </form>
  );
};
